import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Avatar, Chip, Grid, Button,
  Divider, CircularProgress, TextField, MenuItem, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  ArrowBack, Save, Phone, Email,
  LocationOn, CalendarMonth, Business, CheckCircle, PhotoCamera,
  Delete, ArrowForward, AttachMoney, AccountBalance, Warning,
  History, CameraAlt, Description, UploadFile, InsertDriveFile,
} from '@mui/icons-material';
import { InvokeApi, postImg, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import { useAuth } from '../../context/AuthContext';
import dayjs from 'dayjs';

const TABS = [
  { id: 'Personal',   label: 'Personal Details' },
  { id: 'Attendance', label: 'Attendance' },
  { id: 'Salary',     label: 'Salary' },
  { id: 'Fines',      label: 'Fines' },
  { id: 'Bank',       label: 'Bank Details' },
  { id: 'Documents',  label: 'Documents' },
  { id: 'Leaves',     label: 'Leaves' },
  { id: 'ScanLogs',   label: 'Scan Logs' },
  { id: 'AuditLog',   label: 'Audit Log' },
  { id: 'Edit',       label: 'Edit' },
];

const InfoRow = ({ label, value }) => (
  <Box sx={{ py: 1, borderBottom: '1px solid #f5f5f5' }}>
    <Typography fontSize={11} color="text.secondary" fontWeight={500}>{label}</Typography>
    <Typography fontSize={13} fontWeight={600} color={value ? '#1a1a1a' : 'text.disabled'}>{value || '—'}</Typography>
  </Box>
);

const STATUS_STYLE = {
  Approved: { bg: '#e8f5e9', color: '#2e7d32' },
  Rejected: { bg: '#ffebee', color: '#c62828' },
  Pending:  { bg: '#fff3e0', color: '#e65100' },
};

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Personal');
  const [departments, setDepartments] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [attendance, setAttendance] = useState([]);
  const [attYear, setAttYear] = useState(dayjs().year().toString());
  const [attLoading, setAttLoading] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [leavesStatus, setLeavesStatus] = useState('Approved');

  const [salary, setSalary] = useState([]);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [salaryStatus, setSalaryStatus] = useState('Success');
  const [salaryTotal, setSalaryTotal] = useState(0);

  // Salary Calculation
  const [calcMonth, setCalcMonth] = useState(dayjs().month() + 1);
  const [calcYear, setCalcYear] = useState(dayjs().year());
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [salaryStructure, setSalaryStructure] = useState(null);
  const [salaryStructureForm, setSalaryStructureForm] = useState({ base_salary: '', working_hours_per_day: '8' });
  const [savingStructure, setSavingStructure] = useState(false);

  // Fines
  const [fines, setFines] = useState([]);
  const [finesLoading, setFinesLoading] = useState(false);
  const [fineMonth, setFineMonth] = useState(dayjs().month() + 1);
  const [fineYear, setFineYear] = useState(dayjs().year());
  const [fineDialog, setFineDialog] = useState(false);
  const [fineForm, setFineForm] = useState({ fine_amount: '', fine_reason: '', fine_date: dayjs().format('YYYY-MM-DD') });
  const [savingFine, setSavingFine] = useState(false);

  // Bank Details
  const [bankDetails, setBankDetails] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankEditMode, setBankEditMode] = useState(false);
  const [bankForm, setBankForm] = useState({ bank_name:'', account_number:'', ifsc_code:'', account_holder_name:'', branch_name:'', account_type:'Savings', upi_id:'' });
  const [savingBank, setSavingBank] = useState(false);

  // Scan Logs
  const [scanLogs, setScanLogs] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanFromDate, setScanFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [scanToDate, setScanToDate] = useState(dayjs().format('YYYY-MM-DD'));

  // Audit Logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Documents
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docDialog, setDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ title: '', file: null });
  const [docFileName, setDocFileName] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);

  const loadEmployee = useCallback(async () => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getEmployee, { id });
    const e = data?.employee?.[0];
    if (e) {
      setEmp(e);
      setPreviewUrl(e.profile_pic || null);
      setForm({
        employeeId: e.ID,
        fullName: e.name || '',
        password: e.password || '',
        department: e.department || '',
        session: e.session || '',
        age: e.age || '',
        email: e.email || '',
        aadhar: e.aadhar || '',
        gender: e.gender || '',
        designation: e.designation || '',
        address_line1: e.address_line1 || '',
        address_line2: e.address_line2 || '',
        city: e.city || '',
        state: e.state || '',
        pincode: e.pincode || '',
        join_date: e.join_date ? dayjs(e.join_date).format('YYYY-MM-DD') : '',
        resign_date: e.resign_date ? dayjs(e.resign_date).format('YYYY-MM-DD') : '',
        status: e.status || 'Active',
        profile_pic: e.profile_pic_path || '',
        created_by: '1',
      });
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    const loadDepts = async () => {
      const data = await InvokeApi(Config.main_url + apiList.getDeptartments, { status: 'Active' });
      setDepartments(data?.department || []);
    };
    const loadSessions = async () => {
      const data = await InvokeApi(Config.main_url + apiList.getSessions, {});
      setSessions(data?.session || []);
    };
    loadDepts();
    loadSessions();
    loadEmployee();
  }, [loadEmployee]);

  useEffect(() => {
    if (activeTab !== 'Attendance') return;
    const load = async () => {
      setAttLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getAttendance, { studentId: id, year: attYear });
      setAttendance(data?.attendance || []);
      setAttLoading(false);
    };
    load();
  }, [activeTab, id, attYear]);

  useEffect(() => {
    if (activeTab !== 'Leaves') return;
    const load = async () => {
      setLeavesLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getLeavesEmployee, { id, status: leavesStatus });
      setLeaves(data?.leaves || []);
      setLeavesLoading(false);
    };
    load();
  }, [activeTab, id, leavesStatus]);

  useEffect(() => {
    if (activeTab !== 'Salary') return;
    const load = async () => {
      setSalaryLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getFees, { stdId: id, status: salaryStatus });
      const fees = data?.fees || [];
      setSalary(fees);
      setSalaryTotal(fees.reduce((sum, r) => sum + parseFloat(r.salary || 0), 0));
      // Also load salary structure
      const structData = await InvokeApi(Config.main_url + apiList.getSalaryStructure, { employee_id: id });
      if (structData?.salary_structure) {
        setSalaryStructure(structData.salary_structure);
        setSalaryStructureForm({
          base_salary: structData.salary_structure.base_salary || '',
          working_hours_per_day: structData.salary_structure.working_hours_per_day || '8',
        });
      }
      setSalaryLoading(false);
    };
    load();
  }, [activeTab, id, salaryStatus]);

  useEffect(() => {
    if (activeTab !== 'Fines') return;
    const load = async () => {
      setFinesLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getFines, { employee_id: id, month: fineMonth, year: fineYear });
      setFines(data?.fines || []);
      setFinesLoading(false);
    };
    load();
  }, [activeTab, id, fineMonth, fineYear]);

  useEffect(() => {
    if (activeTab !== 'Bank') return;
    const load = async () => {
      setBankLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getBankDetails, { employee_id: id });
      if (data?.bank_details) {
        setBankDetails(data.bank_details);
        setBankForm({
          bank_name: data.bank_details.bank_name || '',
          account_number: data.bank_details.account_number || '',
          ifsc_code: data.bank_details.ifsc_code || '',
          account_holder_name: data.bank_details.account_holder_name || '',
          branch_name: data.bank_details.branch_name || '',
          account_type: data.bank_details.account_type || 'Savings',
          upi_id: data.bank_details.upi_id || '',
        });
      }
      setBankLoading(false);
    };
    load();
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab !== 'ScanLogs') return;
    const load = async () => {
      setScanLoading(true);
      setScanLogs([]);
      const data = await InvokeApi(Config.main_url + apiList.getScanLogs, {
        employee_id: id,
        from_date: scanFromDate,
        to_date: scanToDate,
        limit: 100,
      });
      setScanLogs(data?.scan_logs || []);
      setScanLoading(false);
    };
    load();
  }, [activeTab, id, scanFromDate, scanToDate]);

  useEffect(() => {
    if (activeTab !== 'AuditLog') return;
    const load = async () => {
      setAuditLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getAuditLogs, { employee_id: id, limit: 100 });
      setAuditLogs(data?.logs || []);
      setAuditLoading(false);
    };
    load();
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab !== 'Documents') return;
    const load = async () => {
      setDocsLoading(true);
      const data = await InvokeApi(Config.main_url + apiList.getEmployeeDocuments, { employee_id: id });
      setDocuments(data?.documents || []);
      setDocsLoading(false);
    };
    load();
  }, [activeTab, id]);

  const handleAddDocument = async () => {
    if (!docForm.title.trim()) { alert('Please enter a document title'); return; }
    if (!docForm.file) { alert('Please select a file'); return; }
    setSavingDoc(true);
    try {
      const formData = new FormData();
      formData.append('employee_id', id);
      formData.append('title', docForm.title.trim());
      formData.append('document', docForm.file);
      formData.append('uploaded_by', user?.adminId || '1');
      formData.append('uploaded_by_name', user?.name || 'Admin');
      const data = await postImg(Config.main_url + apiList.addEmployeeDocument, formData);
      if (data?.code === '200') {
        setDocDialog(false);
        setDocForm({ title: '', file: null });
        setDocFileName('');
        const refresh = await InvokeApi(Config.main_url + apiList.getEmployeeDocuments, { employee_id: id });
        setDocuments(refresh?.documents || []);
      } else {
        alert(data?.message || 'Upload failed');
      }
    } catch (err) {
      alert('Upload failed: ' + err.message);
    }
    setSavingDoc(false);
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteEmployeeDocument, {
      id: docId,
      deleted_by: user?.adminId || '1',
      deleted_by_name: user?.name || 'Admin',
    });
    if (data?.code === '200') {
      setDocuments(prev => prev.filter(d => d.ID !== docId));
    } else {
      alert(data?.message || 'Delete failed');
    }
  };

  const handleCalculateSalary = async () => {
    setCalcLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.calculateMonthlySalary, { employee_id: id, month: calcMonth, year: calcYear });
    setCalcResult(data?.code === '200' ? data : null);
    if (data?.code !== '200') alert(data?.message || 'Calculation failed');
    setCalcLoading(false);
  };

  const handleSaveSalaryStructure = async () => {
    if (!salaryStructureForm.base_salary) { alert('Enter base salary'); return; }
    setSavingStructure(true);
    const data = await InvokeApi(Config.main_url + apiList.addSalaryStructure, {
      employee_id: id,
      base_salary: salaryStructureForm.base_salary,
      working_hours_per_day: salaryStructureForm.working_hours_per_day,
      created_by: user?.adminId || '1',
    });
    setSavingStructure(false);
    if (data?.code === '200') {
      setSalaryStructure({ base_salary: salaryStructureForm.base_salary, working_hours_per_day: salaryStructureForm.working_hours_per_day });
      alert('Salary structure saved!');
    } else alert(data?.message || 'Failed');
  };

  const handleAddFine = async () => {
    if (!fineForm.fine_amount || !fineForm.fine_date) { alert('Amount and date required'); return; }
    setSavingFine(true);
    const data = await InvokeApi(Config.main_url + apiList.addFine, {
      employee_id: id,
      fine_amount: fineForm.fine_amount,
      fine_reason: fineForm.fine_reason,
      fine_date: fineForm.fine_date,
      added_by: user?.adminId || '1',
      added_by_name: user?.name || 'Admin',
    });
    setSavingFine(false);
    if (data?.code === '200') {
      setFineDialog(false);
      setFineForm({ fine_amount: '', fine_reason: '', fine_date: dayjs().format('YYYY-MM-DD') });
      const refresh = await InvokeApi(Config.main_url + apiList.getFines, { employee_id: id, month: fineMonth, year: fineYear });
      setFines(refresh?.fines || []);
    } else alert(data?.message || 'Failed');
  };

  const handleDeleteFine = async (fineId) => {
    if (!window.confirm('Delete this fine?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteFine, {
      id: fineId,
      deleted_by: user?.adminId || '1',
      deleted_by_name: user?.name || 'Admin',
    });
    if (data?.code === '200') {
      const refresh = await InvokeApi(Config.main_url + apiList.getFines, { employee_id: id, month: fineMonth, year: fineYear });
      setFines(refresh?.fines || []);
    } else alert(data?.message || 'Failed');
  };

  const handleSaveBank = async () => {
    if (!bankForm.bank_name || !bankForm.account_number || !bankForm.ifsc_code || !bankForm.account_holder_name) {
      alert('Bank name, account number, IFSC and account holder name are required');
      return;
    }
    setSavingBank(true);
    const data = await InvokeApi(Config.main_url + apiList.addBankDetails, {
      employee_id: id, ...bankForm, created_by: user?.adminId || '1',
    });
    setSavingBank(false);
    if (data?.code === '200') {
      setBankDetails({ ...bankForm });
      setBankEditMode(false);
      alert('Bank details saved!');
    } else alert(data?.message || 'Failed');
  };

  const [pendingImageName, setPendingImageName] = useState(null);
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const phpForm = new FormData();
      phpForm.append('profile_pic', file);
      const phpData = await postImg(Config.main_url + apiList.addEmployeeProfileImage, phpForm);

      if (phpData?.code === '200' || phpData?.code === 200) {
        const imageName = phpData.imageName;
        const saveData = await InvokeApi(Config.main_url + apiList.editEmployee, { ...form, profile_pic: imageName });
        if (saveData?.code === '200') {
          setForm(p => ({ ...p, profile_pic: imageName }));
          setPendingImageName(null);
          alert('Profile photo updated successfully!');
          loadEmployee();
        } else {
          setPendingImageName(imageName);
          setForm(p => ({ ...p, profile_pic: imageName }));
          alert('Try again.');
        }
      } else {
        alert('PHP upload failed: ' + (phpData?.message || 'Unknown error'));
        return;
      }

      try {
        const pyForm = new FormData();
        pyForm.append('emp_id', id);
        pyForm.append('profile_image', file);
        const pyData = await postImg(Config.mainpy_url + apiList.AddProfileImgStdPy, pyForm);
        if (pyData?.code === '200') {
          alert('Face recognition updated successfully!');
        } else {
          alert('Face recognition update failed: ' + (pyData?.message || 'Unknown error'));
        }
      } catch (pyErr) {
        alert('Face recognition update failed: ' + pyErr.message);
      }

    } catch (err) {
      console.error(err);
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSavePhoto = async () => {
    if (!pendingImageName) return;
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.editEmployee, {
      ...form,
      profile_pic: pendingImageName,
    });
    setSaving(false);
    if (data?.code === '200') {
      setPendingImageName(null);
      loadEmployee();
      alert('Photo saved!');
    } else {
      alert(data?.message || 'Failed to save photo');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = { ...form };
    if (!payload.profile_pic) {
      delete payload.profile_pic;
    }
    const data = await InvokeApi(Config.main_url + apiList.editEmployee, payload);
    setSaving(false);
    if (data?.code === '200') { loadEmployee(); alert('Updated successfully!'); }
    else alert(data?.message || 'Failed to update');
  };

  const handleDelete = async () => {
    if (!window.confirm('Mark this employee as Resigned?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteEmployee, { id, status: 'Inactive' });
    if (data?.code === '200') navigate('/employees');
    else alert(data?.message || 'Failed');
  };

  const handleApprove = async () => {
    if (!window.confirm('Restore this employee?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteEmployee, { id: id, status: 'Active' });
    if (data?.code === '200') navigate('/employees');
    else alert(data?.message || 'Failed');
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
      <CircularProgress sx={{ color: '#1a237e' }} />
    </Box>
  );

  if (!emp) return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography color="text.secondary">Employee not found</Typography>
      <Button onClick={() => navigate('/employees')} sx={{ mt: 2 }}>Back</Button>
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Tooltip title="Back">
          <IconButton onClick={() => navigate('/employees')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack sx={{ color: '#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">{emp.name}</Typography>
          <Typography variant="body2" color="text.secondary">Emp ID: #{emp.ID} · {emp.department}</Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'sticky', top: 80 }}>
            <Box sx={{ height: 70, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }} />
            <CardContent sx={{ pt: 0, textAlign: 'center', pb: 3 }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mt: -5 }}>
                <Avatar src={previewUrl} sx={{
                  width: 80, height: 80, border: '3px solid #fff',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                  fontSize: 28, fontWeight: 900, color: '#fff',
                }}>
                  {emp.name?.[0]}
                </Avatar>
                <label htmlFor="profile-upload">
                  <input id="profile-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                  <Tooltip title="Change Photo">
                    <IconButton component="span" size="small" disabled={uploading} sx={{
                      position: 'absolute', bottom: -2, right: -2,
                      background: '#1a237e', color: '#fff', width: 26, height: 26,
                      '&:hover': { background: '#1565c0' },
                    }}>
                      {uploading ? <CircularProgress size={12} color="inherit" /> : <PhotoCamera sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Tooltip>
                </label>
              </Box>

              <Typography fontWeight={800} fontSize={15} mt={1.5}>{emp.name}</Typography>
              <Typography fontSize={12} color="text.secondary">{emp.designation || 'Employee'}</Typography>

              {pendingImageName && (
                <Button size="small" variant="contained" onClick={handleSavePhoto} disabled={saving}
                  sx={{ mt: 1, background: '#2e7d32', borderRadius: 2, fontSize: 11, fontWeight: 700 }}>
                  {saving ? <CircularProgress size={14} color="inherit" /> : '💾 Save Photo'}
                </Button>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                <Chip label={`#${emp.ID}`} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 10 }} />
                <Chip label={emp.status} size="small" icon={<CheckCircle sx={{ fontSize: '12px !important' }} />}
                  sx={{ background: emp.status === 'Active' ? '#e8f5e9' : '#ffebee', color: emp.status === 'Active' ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: 10 }} />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ textAlign: 'left', px: 1 }}>
                {[
                  { icon: <Phone fontSize="small" />, value: emp.phone },
                  { icon: <Email fontSize="small" />, value: emp.email },
                  { icon: <Business fontSize="small" />, value: emp.department },
                  { icon: <CalendarMonth fontSize="small" />, value: emp.join_date ? dayjs(emp.join_date).format('DD MMM YYYY') : null },
                  { icon: <LocationOn fontSize="small" />, value: [emp.city, emp.state].filter(Boolean).join(', ') },
                ].map((r, i) => r.value ? (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
                    <Box sx={{ color: '#1a237e', flexShrink: 0 }}>{r.icon}</Box>
                    <Typography fontSize={12} color="text.secondary" sx={{ wordBreak: 'break-all' }}>{r.value}</Typography>
                  </Box>
                ) : null)}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/*<Button fullWidth variant="outlined" startIcon={<Delete />} onClick={handleDelete}
                sx={{ borderRadius: 2, color: '#c62828', borderColor: '#c62828', fontSize: 12, fontWeight: 600,
                  '&:hover': { background: '#ffebee' } }}>
                Mark Resigned
              </Button>*/}
              <Button fullWidth variant="outlined" startIcon={emp.status == 'Resigned' ?  '' : <Delete />} onClick={emp.status == 'Resigned' ?  handleApprove : handleDelete}
                sx={{ borderRadius: 2, color: emp.status == 'Resigned' ? '#2e7d32' : '#c62828', borderColor: emp.status == 'Resigned' ? '#2e7d32' : '#c62828', fontSize: 12, fontWeight: 600, '&:hover': { background: emp.status == 'Resigned' ? '#c4f5c7' : '#ffebee' } }}>
                {emp.status == 'Resigned' ? 'Restore' : 'Delete' } Employee
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={9}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {TABS.map(t => (
              <Box key={t.id} onClick={() => setActiveTab(t.id)} sx={{
                display: 'flex', alignItems: 'center', gap: 0.8, px: 2, py: 1,
                borderRadius: 10, cursor: 'pointer', border: '1.5px solid',
                borderColor: activeTab === t.id ? '#1a237e' : '#e8eaf6',
                background: activeTab === t.id ? '#e8eaf6' : '#fff',
                transition: 'all 0.2s',
              }}>
                {/* <Typography fontSize={14}>{t.icon}</Typography> */}
                <Typography fontSize={13} fontWeight={700} color={activeTab === t.id ? '#1a237e' : 'text.secondary'}>
                  {t.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {activeTab === 'Personal' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Employee Information</Typography>
                <Grid container spacing={0}>
                  {[
                    { label: 'Full Name', value: emp.name },
                    { label: 'Phone', value: emp.phone },
                    { label: 'Password', value: emp.password },
                    { label: 'Email', value: emp.email },
                    { label: 'Gender', value: emp.gender },
                    { label: 'Age', value: emp.age },
                    { label: 'Aadhar Number', value: emp.aadhar },
                    { label: 'Designation', value: emp.designation },
                    { label: 'Department', value: emp.department },
                    { label: 'Session', value: emp.session },
                    { label: 'Address Line 1', value: emp.address_line1 },
                    { label: 'Address Line 2', value: emp.address_line2 },
                    { label: 'City', value: emp.city },
                    { label: 'State', value: emp.state },
                    { label: 'Pincode', value: emp.pincode },
                    { label: 'Join Date', value: emp.join_date ? dayjs(emp.join_date).format('DD MMM YYYY') : null },
                    { label: 'Status', value: emp.status },
                  ].map(({ label, value }) => (
                    <Grid item xs={12} sm={6} key={label} sx={{ px: 1 }}>
                      <InfoRow label={label} value={value} />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {activeTab === 'Attendance' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Attendance Record</Typography>
                  <TextField select size="small" label="Year" value={attYear}
                    onChange={(e) => setAttYear(e.target.value)} sx={{ minWidth: 100 }}>
                    {[dayjs().year(), dayjs().year() - 1, dayjs().year() - 2].map(y => (
                      <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                {attLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : attendance.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>No attendance data for {attYear}</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: '#f5f6fa' }}>
                          {['Month', 'Present', 'Absent', 'Fine', 'Half Day', 'Leave', 'Week Off', ''].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attendance.map((row) => {
                          const a = row.attendance?.[0] || {};
                          return (
                            <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/employees/${id}/attendance/${row.id}/${attYear}`)}>
                              <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>{row.month}</TableCell>
                              <TableCell><Chip label={a.present ?? 0} size="small" sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 10, height: 20 }} /></TableCell>
                              <TableCell><Chip label={a.absent ?? 0} size="small" sx={{ background: '#ffebee', color: '#c62828', fontWeight: 700, fontSize: 10, height: 20 }} /></TableCell>
                              <TableCell><Chip label={a.late ?? 0} size="small" sx={{ background: '#f3e5f5', color: '#6a1b9a', fontWeight: 700, fontSize: 10, height: 20 }} /></TableCell>
                              <TableCell><Chip label={a.half_day ?? 0} size="small" sx={{ background: '#f5f5f5', color: '#4f2f1e', fontWeight: 700, fontSize: 10, height: 20 }} /></TableCell>
                              <TableCell><Chip label={a.leave ?? 0} size="small" sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 700, fontSize: 10, height: 20 }} /></TableCell>
                              <TableCell><Chip label={a.week_off ?? 0} size="small" sx={{ background: '#e0f2f1', color: '#00695c', fontWeight: 700, fontSize: 10, height: 20 }} /></TableCell>
                              <TableCell>
                                <Tooltip title="View Details">
                                  <IconButton size="small" sx={{ color: '#1a237e' }}>
                                    <ArrowForward fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'Salary' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Salary Structure */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Salary Structure</Typography>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Base Monthly Salary (₹)" type="number"
                        value={salaryStructureForm.base_salary}
                        onChange={e => setSalaryStructureForm(p => ({ ...p, base_salary: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Working Hours/Day" type="number"
                        value={salaryStructureForm.working_hours_per_day}
                        onChange={e => setSalaryStructureForm(p => ({ ...p, working_hours_per_day: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button fullWidth variant="contained" onClick={handleSaveSalaryStructure} disabled={savingStructure}
                        startIcon={savingStructure ? <CircularProgress size={16} color="inherit" /> : <Save />}
                        sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 2, fontWeight: 700 }}>
                        {savingStructure ? 'Saving...' : 'Save Structure'}
                      </Button>
                    </Grid>
                  </Grid>
                  {salaryStructure && (
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, background: '#e8f5e9', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Typography fontSize={13} color="#2e7d32">
                        <b>Base:</b> ₹{parseFloat(salaryStructure.base_salary).toLocaleString()}
                      </Typography>
                      <Typography fontSize={13} color="#2e7d32">
                        <b>Hours/Day:</b> {salaryStructure.working_hours_per_day}h
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Monthly Salary Calculator */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Monthly Salary Calculator</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end', mb: 2 }}>
                    <TextField select size="small" label="Month" value={calcMonth} onChange={e => setCalcMonth(e.target.value)} sx={{ minWidth: 130 }}>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m,i) => (
                        <MenuItem key={i+1} value={i+1}>{m}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select size="small" label="Year" value={calcYear} onChange={e => setCalcYear(e.target.value)} sx={{ minWidth: 100 }}>
                      {[dayjs().year(), dayjs().year()-1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </TextField>
                    <Button variant="contained" onClick={handleCalculateSalary} disabled={calcLoading}
                      startIcon={calcLoading ? <CircularProgress size={16} color="inherit" /> : <AttachMoney />}
                      sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                      {calcLoading ? 'Calculating...' : 'Calculate'}
                    </Button>
                  </Box>

                  {calcResult && (
                    <Box>
                      <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', mb: 2 }}>
                        <Typography fontSize={13} sx={{ opacity: 0.8 }}>{calcResult.month_name} {calcResult.year} — Net Salary</Typography>
                        <Typography fontSize={28} fontWeight={900}>₹{parseFloat(calcResult.net_salary).toLocaleString()}</Typography>
                      </Box>
                      <Grid container spacing={1.5}>
                        {[
                          { label: 'Base Salary', value: `₹${parseFloat(calcResult.base_salary).toLocaleString()}`, color: '#1a237e' },
                          { label: 'Days in Month', value: calcResult.days_in_month, color: '#555' },
                          { label: 'Daily Rate', value: `₹${parseFloat(calcResult.daily_rate).toFixed(2)}`, color: '#555' },
                          { label: 'Hourly Rate', value: `₹${parseFloat(calcResult.hourly_rate).toFixed(2)}`, color: '#555' },
                          { label: 'Present Days', value: calcResult.attendance?.present_days, color: '#2e7d32' },
                          { label: 'Absent Days', value: calcResult.attendance?.absent_days, color: '#c62828' },
                          { label: 'Leave Days', value: calcResult.attendance?.leave_days, color: '#e65100' },
                          { label: 'Week Off', value: calcResult.attendance?.week_off_days, color: '#00695c' },
                          { label: 'Absent Deduction', value: `₹${parseFloat(calcResult.deductions?.absent_deduction_amount).toFixed(2)}`, color: '#c62828' },
                          { label: 'Time Deduction (hrs)', value: `${calcResult.deductions?.deduction_hours}h`, color: '#c62828' },
                          { label: 'Time Deduction (₹)', value: `₹${parseFloat(calcResult.deductions?.time_deduction_amount).toFixed(2)}`, color: '#c62828' },
                          { label: 'Fines', value: `₹${parseFloat(calcResult.deductions?.fines_amount).toFixed(2)}`, color: '#c62828' },
                        ].map(({ label, value, color }) => (
                          <Grid item xs={6} sm={4} md={3} key={label}>
                            <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #f0f0f0', background: '#fafafa' }}>
                              <Typography fontSize={10} color="text.secondary" fontWeight={500}>{label}</Typography>
                              <Typography fontSize={14} fontWeight={700} color={color}>{value ?? '—'}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Salary Records */}
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700} color="#1a237e">Salary Records</Typography>
                      {!salaryLoading && salary.length > 0 && (
                        <Typography fontSize={13} color="text.secondary">
                          {salary.length} records · Total: ₹{salaryTotal.toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                    <TextField select size="small" label="Status" value={salaryStatus}
                      onChange={(e) => setSalaryStatus(e.target.value)} sx={{ minWidth: 130 }}>
                      {['Success', 'Pending', 'Failed'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  </Box>

                  {salaryLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} size={32} />
                    </Box>
                  ) : salary.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <AttachMoney sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                      <Typography color="text.secondary">No {salaryStatus.toLowerCase()} salary records</Typography>
                    </Box>
                  ) : (
                    <Box>
                      {salary.map((r, i) => {
                        const statusStyle = {
                          Success: { bg: '#e8f5e9', color: '#2e7d32' },
                          Pending: { bg: '#fff3e0', color: '#e65100' },
                          Failed:  { bg: '#ffebee', color: '#c62828' },
                        }[r.status] || { bg: '#f5f5f5', color: '#666' };
                        return (
                          <Box key={r.ID || i} sx={{
                            display: 'flex', alignItems: 'center', gap: 2,
                            p: 1.5, mb: 1, borderRadius: 2,
                            border: '1px solid #f0f0f0',
                            borderLeft: `4px solid ${statusStyle.color}`,
                            background: '#fff',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                          }}>
                            <Box sx={{
                              width: 52, height: 52, borderRadius: 2, flexShrink: 0,
                              background: statusStyle.bg,
                              display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center',
                            }}>
                              <AttachMoney sx={{ color: statusStyle.color, fontSize: 20 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                                <Typography fontWeight={700} fontSize={15} color={statusStyle.color}>
                                  ₹{parseFloat(r.salary || 0).toLocaleString()}
                                </Typography>
                                <Chip label={r.status} size="small"
                                  sx={{ background: statusStyle.bg, color: statusStyle.color, fontWeight: 600, fontSize: 10, height: 18 }} />
                              </Box>
                              <Typography fontSize={12} color="text.secondary">
                                Receipt: {r.recipt_no || '—'} · {r.recipt_name || '—'}
                              </Typography>
                              <Typography fontSize={11} color="text.disabled">
                                {r.created_at ? dayjs(r.created_at).format('DD MMM YYYY') : '—'}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          {activeTab === 'Fines' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Fines</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField select size="small" label="Month" value={fineMonth} onChange={e => setFineMonth(e.target.value)} sx={{ minWidth: 110 }}>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m,i) => (
                        <MenuItem key={i+1} value={i+1}>{m}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select size="small" label="Year" value={fineYear} onChange={e => setFineYear(e.target.value)} sx={{ minWidth: 90 }}>
                      {[dayjs().year(), dayjs().year()-1].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </TextField>
                    <Button variant="contained" startIcon={<Warning />} onClick={() => setFineDialog(true)}
                      sx={{ background: 'linear-gradient(135deg,#c62828,#e53935)', borderRadius: 2, fontWeight: 700, fontSize: 12 }}>
                      Add Fine
                    </Button>
                  </Box>
                </Box>
                {finesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : fines.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>No fines for this period</Typography>
                ) : (
                  <Box>
                    <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#ffebee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography fontWeight={700} color="#c62828">Total Fines</Typography>
                      <Typography fontWeight={800} fontSize={18} color="#c62828">
                        ₹{fines.reduce((s,f) => s + parseFloat(f.fine_amount||0), 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: '#f5f6fa' }}>
                            {['Date','Amount','Reason','Added By','Action'].map(h => (
                              <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fines.map(fine => (
                            <TableRow key={fine.ID} hover>
                              <TableCell sx={{ fontSize: 12 }}>{fine.fine_date ? dayjs(fine.fine_date).format('DD MMM YYYY') : '—'}</TableCell>
                              <TableCell>
                                <Chip label={`₹${parseFloat(fine.fine_amount).toLocaleString()}`} size="small"
                                  sx={{ background: '#ffebee', color: '#c62828', fontWeight: 700, fontSize: 11 }} />
                              </TableCell>
                              <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{fine.fine_reason || '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{fine.added_by_name || '—'}</TableCell>
                              <TableCell>
                                <Tooltip title="Delete Fine">
                                  <IconButton size="small" onClick={() => handleDeleteFine(fine.ID)} sx={{ color: '#c62828' }}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'Bank' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Bank Details</Typography>
                  {!bankEditMode && (
                    <Button variant="outlined" onClick={() => setBankEditMode(true)}
                      sx={{ borderRadius: 2, fontWeight: 700, fontSize: 12 }}>
                      {bankDetails ? 'Edit' : 'Add Bank Details'}
                    </Button>
                  )}
                </Box>
                {bankLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : bankEditMode ? (
                  <Grid container spacing={2}>
                    {[
                      { label: 'Bank Name *', key: 'bank_name' },
                      { label: 'Account Number *', key: 'account_number' },
                      { label: 'IFSC Code *', key: 'ifsc_code' },
                      { label: 'Account Holder Name *', key: 'account_holder_name' },
                      { label: 'Branch Name', key: 'branch_name' },
                      { label: 'UPI ID', key: 'upi_id' },
                    ].map(({ label, key }) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <TextField fullWidth size="small" label={label} value={bankForm[key] || ''}
                          onChange={e => setBankForm(p => ({ ...p, [key]: e.target.value }))} />
                      </Grid>
                    ))}
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth size="small" label="Account Type" value={bankForm.account_type}
                        onChange={e => setBankForm(p => ({ ...p, account_type: e.target.value }))}>
                        {['Savings','Current','Salary'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" startIcon={savingBank ? <CircularProgress size={16} color="inherit" /> : <Save />}
                          onClick={handleSaveBank} disabled={savingBank}
                          sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                          {savingBank ? 'Saving...' : 'Save Bank Details'}
                        </Button>
                        <Button variant="outlined" onClick={() => setBankEditMode(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
                      </Box>
                    </Grid>
                  </Grid>
                ) : bankDetails ? (
                  <Grid container spacing={0}>
                    {[
                      { label: 'Bank Name', value: bankDetails.bank_name },
                      { label: 'Account Number', value: bankDetails.account_number },
                      { label: 'IFSC Code', value: bankDetails.ifsc_code },
                      { label: 'Account Holder', value: bankDetails.account_holder_name },
                      { label: 'Branch', value: bankDetails.branch_name },
                      { label: 'Account Type', value: bankDetails.account_type },
                      { label: 'UPI ID', value: bankDetails.upi_id },
                    ].map(({ label, value }) => (
                      <Grid item xs={12} sm={6} key={label} sx={{ px: 1 }}>
                        <Box sx={{ py: 1, borderBottom: '1px solid #f5f5f5' }}>
                          <Typography fontSize={11} color="text.secondary" fontWeight={500}>{label}</Typography>
                          <Typography fontSize={13} fontWeight={600}>{value || '—'}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AccountBalance sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary">No bank details added yet</Typography>
                    <Button variant="contained" onClick={() => setBankEditMode(true)} sx={{ mt: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                      Add Bank Details
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'Documents' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Documents</Typography>
                  <Button variant="contained" startIcon={<UploadFile />} onClick={() => setDocDialog(true)}
                    sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, fontSize: 12 }}>
                    Add Document
                  </Button>
                </Box>

                {docsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress sx={{ color: '#1a237e' }} size={32} />
                  </Box>
                ) : documents.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Description sx={{ fontSize: 56, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary" fontWeight={500}>No documents uploaded yet</Typography>
                    <Button variant="contained" startIcon={<UploadFile />} onClick={() => setDocDialog(true)}
                      sx={{ mt: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                      Upload First Document
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {documents.map((doc) => {
                      const isImage = doc.file_type?.startsWith('image/');
                      const isPdf   = doc.file_type === 'application/pdf';
                      const fileUrl = Config.main_url + '/' + doc.file_path;
                      return (
                        <Box key={doc.ID} sx={{
                          display: 'flex', alignItems: 'center', gap: 2,
                          p: 2, borderRadius: 2,
                          border: '1px solid #e8eaf6',
                          borderLeft: '4px solid #1a237e',
                          background: '#fff',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        }}>
                          <Box sx={{
                            width: 44, height: 44, borderRadius: 2, flexShrink: 0,
                            background: isPdf ? '#ffebee' : isImage ? '#e8f5e9' : '#e8eaf6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <InsertDriveFile sx={{
                              fontSize: 24,
                              color: isPdf ? '#c62828' : isImage ? '#2e7d32' : '#1a237e',
                            }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontWeight={700} fontSize={14} color="#1a237e" noWrap>
                              {doc.title}
                            </Typography>
                            <Typography fontSize={11} color="text.secondary" noWrap>
                              {doc.file_name}
                              {/* {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(1)} KB` : ''} */}
                            </Typography>
                            <Typography fontSize={11} color="text.disabled">
                              {doc.uploaded_by_name || 'Admin'} · {doc.created_at ? dayjs(doc.created_at).format('DD MMM YYYY') : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <Tooltip title="View / Download">
                              <IconButton size="small" onClick={() => window.open(fileUrl, '_blank')}
                                sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 1.5 }}>
                                <Description fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Document">
                              <IconButton size="small" onClick={() => handleDeleteDocument(doc.ID)}
                                sx={{ color: '#c62828', border: '1px solid #ffebee', borderRadius: 1.5 }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'ScanLogs' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Attendance Scan Logs</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <TextField size="small" label="From" type="date" value={scanFromDate}
                      onChange={e => setScanFromDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
                    <TextField size="small" label="To" type="date" value={scanToDate}
                      onChange={e => setScanToDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
                  </Box>
                </Box>
                {scanLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : scanLogs.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CameraAlt sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary">No scan logs found for this period</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {scanLogs.map(log => {
                      const isCheckIn   = log.scan_type === 'check_in';
                      const isFaceScan  = !!log.captured_image_url;

                      const typeStyles = {
                        check_in:  { color: '#2e7d32', bg: '#e8f5e9', label: '↑ Check In' },
                        check_out: { color: '#e65100', bg: '#fff3e0', label: '↓ Check Out' },
                        absent:    { color: '#c62828', bg: '#ffebee', label: '✗ Absent' },
                        leave:     { color: '#e65100', bg: '#fff3e0', label: '🏖 Leave' },
                        week_off:  { color: '#00695c', bg: '#e0f2f1', label: '📅 Week Off' },
                        present:   { color: '#2e7d32', bg: '#e8f5e9', label: '✓ Present' },
                        late:      { color: '#6a1b9a', bg: '#f3e5f5', label: '⏰ Late' },
                      };
                      const ts       = typeStyles[log.scan_type] || { color: '#555', bg: '#f5f5f5', label: log.scan_type };
                      const typeColor = ts.color;
                      const typeBg    = ts.bg;
                      return (
                        <Box key={log.ID} sx={{
                          display: 'flex', gap: 2, p: 2, borderRadius: 2,
                          border: `1px solid ${typeColor}20`,
                          borderLeft: `4px solid ${typeColor}`,
                          background: '#fff',
                          boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
                          alignItems: 'flex-start',
                        }}>
                          {/* Image — only if captured */}
                          {isFaceScan && (
                            <Box
                              component="img"
                              src={log.captured_image_url}
                              alt="scan"
                              onClick={() => window.open(log.captured_image_url, '_blank')}
                              sx={{
                                width: 64, height: 64, borderRadius: 2, objectFit: 'cover',
                                border: `2px solid ${typeColor}40`, cursor: 'pointer', flexShrink: 0,
                              }}
                            />
                          )}

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Type chip + time */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={ts.label}
                                size="small"
                                sx={{ background: typeBg, color: typeColor, fontWeight: 800, fontSize: 11 }}
                              />
                              <Typography fontSize={15} fontWeight={800} color={typeColor}>
                                {log.scan_time ? dayjs(log.scan_time).format('hh:mm A') : '—'}
                              </Typography>
                              <Typography fontSize={11} color="text.secondary">
                                {log.scan_time ? dayjs(log.scan_time).format('DD MMM YYYY') : ''}
                              </Typography>
                            </Box>

                            {/* Source — face scan or manual */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CameraAlt sx={{ fontSize: 13, color: isFaceScan ? '#00695c' : '#9e9e9e' }} />
                              <Typography fontSize={12} color={isFaceScan ? '#00695c' : 'text.secondary'} fontWeight={600}>
                                {isFaceScan
                                  ? `Scanned by ${log.employee_name || 'Employee'}`
                                  : `Marked by ${(log.device_info || 'Admin').replace(/^Marked by\s*/i, '')}`}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'AuditLog' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Audit Log</Typography>
                {auditLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : auditLogs.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <History sx={{ fontSize: 48, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary">No audit logs for this employee</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: '#f5f6fa' }}>
                          {['Time','Action','Value','Note','Done By'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {auditLogs.map(log => {
                          const actionColors = {
                            fine_add:        { bg:'#ffebee', color:'#c62828', label:'Fine Added' },
                            fine_delete:     { bg:'#f3e5f5', color:'#6a1b9a', label:'Fine Deleted' },
                            salary_edit:     { bg:'#e3f2fd', color:'#1565c0', label:'Salary Edit' },
                            attendance_edit: { bg:'#e8f5e9', color:'#2e7d32', label:'Attendance Edit' },
                            time_reduce:     { bg:'#fff3e0', color:'#e65100', label:'Time Reduced' },
                            employee_edit:   { bg:'#e8eaf6', color:'#1a237e', label:'Employee Edit' },
                            bank_edit:       { bg:'#e0f2f1', color:'#00695c', label:'Bank Edit' },
                          };
                          const s = actionColors[log.action_type] || { bg:'#f5f5f5', color:'#555', label: log.action_type?.replace(/_/g,' ') };
                          return (
                            <TableRow key={log.ID} hover>
                              <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                {log.created_at ? dayjs(log.created_at).format('DD MMM YY HH:mm') : '—'}
                              </TableCell>
                              <TableCell>
                                <Chip label={s.label} size="small"
                                  sx={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: 10, textTransform: 'capitalize' }} />
                              </TableCell>
                              <TableCell sx={{ fontSize: 12, color: '#2e7d32', fontWeight: 600, maxWidth: 140, wordBreak: 'break-word' }}>
                                {log.new_value || '—'}
                              </TableCell>
                              <TableCell sx={{ fontSize: 11, color: 'text.secondary', maxWidth: 180, wordBreak: 'break-word' }}>
                                {log.note || '—'}
                              </TableCell>
                              <TableCell>
                                <Typography fontSize={12} fontWeight={700} color="#1a237e">
                                  {log.done_by_name || '—'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={log.actor_type === 'employee' ? 'Employee' : (log.done_by_role || 'Admin')}
                                    size="small"
                                    sx={{
                                      background: log.actor_type === 'employee' ? '#e8f5e9' : '#e8eaf6',
                                      color:      log.actor_type === 'employee' ? '#2e7d32' : '#1a237e',
                                      fontWeight: 600, fontSize: 10,
                                    }}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'Leaves' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Leave Applications</Typography>
                  <TextField select size="small" label="Status" value={leavesStatus}
                    onChange={(e) => setLeavesStatus(e.target.value)} sx={{ minWidth: 130 }}>
                    {['Approved', 'Pending', 'Rejected'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                </Box>
                {leavesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} size={32} /></Box>
                ) : leaves.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>No {leavesStatus.toLowerCase()} leaves</Typography>
                ) : (
                  <Box>
                    {leaves.map((l, i) => {
                      const s = STATUS_STYLE[l.status] || STATUS_STYLE.Pending;
                      return (
                        <Box key={i}>
                          <Box sx={{ py: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip label={l.leave_type || 'Leave'} size="small"
                                  sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 11 }} />
                                <Chip label={l.status} size="small"
                                  sx={{ background: s.bg, color: s.color, fontWeight: 600, fontSize: 11 }} />
                              </Box>
                              <Typography fontSize={12} color="text.secondary">
                                {l.start_date ? dayjs(l.start_date).format('DD MMM YYYY') : '—'} → {l.end_date ? dayjs(l.end_date).format('DD MMM YYYY') : '—'}
                              </Typography>
                              {l.leave_details && (
                                <Typography fontSize={12} color="text.secondary" mt={0.3}>{l.leave_details}</Typography>
                              )}
                            </Box>
                          </Box>
                          {i < leaves.length - 1 && <Divider />}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'Edit' && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                  <Typography variant="h6" fontWeight={700} color="#1a237e">Edit Employee</Typography>
                  <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                    onClick={handleSave} disabled={saving}
                    sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 2, fontWeight: 700 }}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {[
                    { label: 'Full Name *', key: 'fullName' },
                    { label: 'Password', key: 'password' },
                    // { label: 'Phone', key: 'phone' },
                    { label: 'Email', key: 'email' },
                    { label: 'Aadhar', key: 'aadhar' },
                    { label: 'Age', key: 'age' },
                    { label: 'Designation', key: 'designation' },
                    { label: 'Address Line 1', key: 'address_line1' },
                    { label: 'Address Line 2', key: 'address_line2' },
                    { label: 'City', key: 'city' },
                    { label: 'State', key: 'state' },
                    { label: 'Pincode', key: 'pincode' },
                    { label: 'Join Date', key: 'join_date', type: 'date' },
                  ].map(({ label, key, type }) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <TextField fullWidth size="small" label={label} type={type || 'text'}
                        value={form[key] || ''} onChange={f(key)}
                        InputLabelProps={type === 'date' ? { shrink: true } : {}} />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth size="small" label="Gender" value={form.gender || ''} onChange={f('gender')}>
                      {['Male', 'Female', 'Other'].map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth size="small" label="Department *" value={form.department || ''} onChange={f('department')}>
                      {departments.map(d => <MenuItem key={d.ID} value={d.department}>{d.department}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth size="small" label="Session" value={form.session || ''} onChange={f('session')}>
                      <MenuItem value="">— None —</MenuItem>
                      {sessions.map(s => <MenuItem key={s.ID} value={s.session}>{s.session} ({s.check_in_time} – {s.check_out_time})</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth size="small" label="Status" value={form.status || 'Active'} onChange={f('status')}>
                      {['Active', 'Resigned'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Add Document Dialog */}
      <Dialog open={docDialog} onClose={() => { setDocDialog(false); setDocForm({ title: '', file: null }); setDocFileName(''); }}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Add Document
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Document Title *"
                value={docForm.title}
                onChange={e => setDocForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Aadhar Card, Offer Letter, Certificate" />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{
                border: '2px dashed #c5cae9', borderRadius: 2, p: 2.5,
                textAlign: 'center', cursor: 'pointer',
                background: docFileName ? '#e8eaf6' : '#fafafa',
                '&:hover': { borderColor: '#1a237e', background: '#e8eaf6' },
              }}
                onClick={() => document.getElementById('doc-file-input').click()}>
                <input
                  id="doc-file-input"
                  type="file"
                  style={{ display: 'none' }}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { setDocForm(p => ({ ...p, file: f })); setDocFileName(f.name); }
                  }}
                />
                <UploadFile sx={{ fontSize: 32, color: docFileName ? '#1a237e' : '#9e9e9e', mb: 0.5 }} />
                <Typography fontSize={13} fontWeight={600} color={docFileName ? '#1a237e' : 'text.secondary'}>
                  {docFileName || 'Click to select file'}
                </Typography>
                <Typography fontSize={11} color="text.disabled" mt={0.3}>
                  PDF, Images, Word, Excel, Text · Max 10 MB
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => { setDocDialog(false); setDocForm({ title: '', file: null }); setDocFileName(''); }}
            variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleAddDocument} variant="contained" disabled={savingDoc}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {savingDoc ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Fine Dialog */}
      <Dialog open={fineDialog} onClose={() => setFineDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#c62828', borderBottom: '1px solid #f0f0f0' }}>
          Add Fine
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Fine Amount (₹) *" type="number"
                value={fineForm.fine_amount} onChange={e => setFineForm(p => ({ ...p, fine_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Fine Date *" type="date"
                value={fineForm.fine_date} onChange={e => setFineForm(p => ({ ...p, fine_date: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reason" multiline rows={2}
                value={fineForm.fine_reason} onChange={e => setFineForm(p => ({ ...p, fine_reason: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setFineDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleAddFine} variant="contained" disabled={savingFine}
            sx={{ background: 'linear-gradient(135deg,#c62828,#e53935)', borderRadius: 2, fontWeight: 700 }}>
            {savingFine ? <CircularProgress size={20} color="inherit" /> : 'Add Fine'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
